import React, { useEffect, useState } from 'react'
import { Alert, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { AccountTreeOutlined, DeleteOutline, SchemaOutlined } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../generic/Api_Functions'
import OperationLibraryVersions from './OperationLibraryVersions'
import OperationUsagesDialog from './OperationUsagesDialog'
import { deleteOperation, readOperationLibraryError, searchOperationLibrary } from './operationLibraryApi'

/**
 * OperationLibraryTable (C1, §8.7) — the OPERATIONS view of the Workflow 2.0 editor: the library
 * listing that REPLACES the graph while `type = OPERATIONS` is selected (S9). Built like the other
 * paginated listings of the repo (`BomLibrary.jsx` of story 117): ONE `SmartMateriaUITable` over ONE
 * endpoint, `POST operationLibrary/search` (N1), fed with the table's own RequestPagination — the
 * search term included, which travels in its standard `genericSearch` field (contrato-sun.md §7).
 *
 * One row per CODE, reading the fields of the current version (`currentVersionNumber`,
 * `currentIndexValue`, …), which Sun resolves server-side. Default order `code` ASC and standard
 * pagination (RN-01.2b, S114); the user may reorder by any column, and the default comes back when
 * the view is entered again, because the ordering state lives in the table instance.
 *
 * An empty library is an empty table, never an error (S12).
 *
 * "New operation" (K4) creates version 1 through the edit scope (N8 with `targetCode` null, S14):
 * name and description are OPTIONAL, and a duplicate code is refused by Sun with E-OPL-02, after
 * which the client offers the correct path — a new version of the existing operation (S16).
 */
const OperationLibraryTable = ({
  useIndex = true,
  canEdit = false,
  selectedNodeId = '',
  onVersionSelected = () => {},
  onCreateDraft = () => {},
  onCommitted = () => {},
  createError = '',
  reloadKey = 0
}) => {
  const { t_ } = useSmartTranslation()
  const [versionsOf, setVersionsOf] = useState({})
  const [showVersions, setShowVersions] = useState(false)
  // Story 184 extension (CA14): the usages dialog opened from the "Where used" row action. `{}` = closed.
  const [usagesOf, setUsagesOf] = useState({})
  const [isEmpty, setIsEmpty] = useState(false)
  const [ownReloadKey, setOwnReloadKey] = useState(0)
  const [showCreate, setShowCreate] = useState(false)
  const [draft, setDraft] = useState({ code: '', name: '', description: '', indexValue: '', technologyName: '' })
  const [indexOptions, setIndexOptions] = useState([])
  const [technologies, setTechnologies] = useState([])
  const [pendingDelete, setPendingDelete] = useState({})
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => {
    const loadIndexes = async () => {
      if (!useIndex) return
      // Los valores que los permisos `WORKFLOW_INDEX_*` del usuario conceden (RN-G21); con el flag
      // desactivado no hay dimensión de index que preguntar (S19).
      const available = await apiGet('indexValues/available')
      setIndexOptions(Array.isArray(available) ? available : [])
    }
    loadIndexes()
  }, [useIndex])

  useEffect(() => {
    // Las mismas tecnologías que ofrece la paleta del editor: una operación es un JOB, y un JOB nace
    // de una tecnología. Sin ella el job saldría incompleto y Sun lo rechazaría al guardar.
    // `technologiesWorkflow/noPagination` devuelve la LISTA entera (patrón `programs/Programs.jsx`):
    // la ruta paginada exige la `RequestPagination` completa de la tabla y aquí no hay tabla.
    const loadTechnologies = async () => {
      const list = await apiGet('technologiesWorkflow/noPagination')
      setTechnologies(Array.isArray(list) ? list : [])
    }
    loadTechnologies()
  }, [])

  useEffect(() => {
    // El alta la cierra el guardado (N8), que es quien puede rechazarla: si lo hace, el diálogo
    // vuelve con el literal y con los datos que el usuario ya había puesto.
    if (createError) setShowCreate(true)
  }, [createError])

  const dataFetch = async (pagination) => {
    const page = await searchOperationLibrary(pagination)
    setIsEmpty((page?.content?.length || 0) === 0)
    return page
  }

  const reload = () => setOwnReloadKey((key) => key + 1)

  // El index por defecto es el primero que los permisos conceden. Se resuelve en el momento de
  // pintar y de guardar, no al abrir: la lista puede llegar después (RN-G21).
  const effectiveIndexValue = draft.indexValue || indexOptions[0]?.name || ''

  const openCreate = () => {
    setDraft({ code: '', name: '', description: '', indexValue: '', technologyName: '' })
    setShowCreate(true)
  }

  const createOperation = () => {
    // Ajuste del PO (2026-09-15): el alta NO escribe. Prepara la operación —código, nombre,
    // descripción, index y tecnología— y la deja en el editor para configurarla (tareas, preparación,
    // recursos) ANTES de guardar. Lo que escribe es el Save de la vista (N8).
    const technology = technologies.find((option) => option.name === draft.technologyName)
    if (!draft.code || !technology) return
    setShowCreate(false)
    onCreateDraft({
      code: draft.code,
      name: draft.name,
      description: draft.description,
      indexValue: useIndex ? effectiveIndexValue : 'APPROVED',
      technology
    })
  }

  const removeOperation = async () => {
    setDeleteError('')
    try {
      await deleteOperation(pendingDelete.code)
      setPendingDelete({})
      reload()
      onCommitted()
    } catch (thrown) {
      setDeleteError(readOperationLibraryError(thrown).error)
    }
  }

  const columns = [
    { name: t_('Code'), field: 'code', mandatory: true, unique: true },
    { name: t_('Name'), field: 'name' },
    { name: t_('Description'), field: 'description' },
    // Story 184 extension (CA12 · RN-09): the technology of the CURRENT version's job, straight from N1
    // `currentTechnologyName` (contrato-sun.md). It sorts (Sun maps the key, RN-11.3) and has no column filter.
    { name: t_('Technology'), field: 'currentTechnologyName', search: false, readOnly: true },
    { name: t_('Version'), field: 'currentVersionNumber', readOnly: true },
    // The index dimension only exists while USE_WORKFLOW_INDEX is on (S19, RN-G22).
    ...(useIndex ? [{ name: t_('Index Value'), field: 'currentIndexValue', readOnly: true }] : []),
    { name: t_('Created'), field: 'currentCreatedAt', type: 'datetime', readOnly: true },
    { name: t_('Created by'), field: 'currentCreatedBy', readOnly: true },
    { name: t_('Used in workflows'), field: 'workflowUsageCount', search: false, readOnly: true }
  ]

  const actions = {
    // El alta vive en la barra de la tabla, como en el resto de listados del repo: es el sitio
    // donde el usuario la busca. `customAddRecordClick` la desvía a nuestro diálogo en vez de a la
    // fila en línea, porque los rótulos del alta son "Operation code" / "Operation name" (S14) y no
    // los de las columnas.
    // Historia 184 (CA9 · RN-06.1, R6): sin `OPERATION_LIBRARY_EDIT` el botón NO se pinta (antes salía
    // deshabilitado): sin `create`, la barra de SmartMateriaUITable no ofrece el alta, como en las tablas sin alta.
    ...(canEdit ? { create: { label: 'New operation' } } : {}),
    custom: [
      {
        name: 'Versions',
        render: () => (
          <IconButton>
            <AccountTreeOutlined />
          </IconButton>
        ),
        onClick: (row) => {
          setVersionsOf(row)
          setShowVersions(true)
        }
      },
      // Story 184 extension (CA14 · RN-10, PO 05/10/2026): "Where used" (existing key, the 8 locales) opens the same
      // usages dialog of the panel for the code of the row, with its CURRENT version highlighted (D6). The count of the
      // "Used in workflows" column stays the plain read-only number it always was.
      {
        name: 'Where used',
        render: () => (
          <IconButton data-testid='operation-table-where-used'>
            <SchemaOutlined />
          </IconButton>
        ),
        onClick: (row) => setUsagesOf({ code: row.code, name: row.name || '', versionNumber: row.currentVersionNumber })
      },
      ...(canEdit
        ? [{
            name: 'Delete operation',
            render: () => (
              <IconButton>
                <DeleteOutline />
              </IconButton>
            ),
            onClick: (row) => {
              setDeleteError('')
              setPendingDelete(row)
            }
          }]
        : [])
    ]
  }

  return (
    <div data-testid='operation-library-table'>
      <SmartMateriaUITable
        tableTuneUniqueKey='operationLibrary'
        title={t_('Operation library')}
        columns={columns}
        dataFetch={dataFetch}
        actions={actions}
        customAddRecordClick
        onAddElementClick={openCreate}
        forceLoad={reloadKey + ownReloadKey}
        sortOptions={{ default: { field: 'code', direction: 1 } }}
        pageable
        primaryKeys={['code']}
      />

      {isEmpty
        ? <Typography data-testid='operation-library-empty' color='primary'>{t_('No operations found')}</Typography>
        : null}

      <SmartDialog
        renderComponent={
          <OperationLibraryVersions
            code={versionsOf.code || ''}
            selectedNodeId={selectedNodeId}
            canEdit={canEdit}
            useIndex={useIndex}
            onChanged={reload}
            onVersionSelected={(version) => { setShowVersions(false); onVersionSelected({ ...version, code: versionsOf.code || version.code }) }}
          />
        }
        setOpen={showVersions}
        cancel={false}
        accept={false}
        close
        title={{ render: <Typography variant='h5'>{(versionsOf.code || '') + (versionsOf.name ? ' · ' + versionsOf.name : '')}</Typography> }}
        closeCallback={() => setShowVersions(false)}
      />

      {/* Story 184 extension (CA14 · D6): the same usages dialog of the panel, from the "Where used" row action; the
          row IS the current version, so that is the one highlighted, as "Current version". */}
      <OperationUsagesDialog
        open={!!usagesOf.code}
        code={usagesOf.code || ''}
        name={usagesOf.name || ''}
        selectedVersionNumber={usagesOf.versionNumber || 0}
        selectedVersionLabel='Current version'
        onClose={() => setUsagesOf({})}
      />

      <SmartDialog
        setOpen={showCreate}
        title={{ render: <Typography variant='h6'>{t_('New operation')}</Typography> }}
        acceptLabel={t_('New operation')}
        acceptCallback={createOperation}
        // Solo el ACEPTAR: `disableButtons` bloquearía también el Cancelar, y de un alta a medias
        // hay que poder salir.
        disableAccept={!draft.code || !draft.technologyName}
        cancelCallback={() => setShowCreate(false)}
        autoClose={false}
        renderComponent={
          <Stack spacing={2} sx={{ mt: 1, minWidth: 320 }} data-testid='operation-create-dialog'>
            {createError
              ? <Alert severity='warning' data-testid='operation-create-error'>{t_(createError)}</Alert>
              : null}
            {createError === 'Operation code already exists in the library'
              ? (
                <Button
                  variant='outlined'
                  data-testid='operation-create-new-version'
                  onClick={() => {
                    setShowCreate(false)
                    setVersionsOf({ code: draft.code })
                    setShowVersions(true)
                  }}
                >
                  {t_('Create a new version of this operation')}
                </Button>
                )
              : null}
            <TextField
              required
              label={t_('Operation code')}
              value={draft.code}
              onChange={(event) => setDraft((current) => ({ ...current, code: event.target.value }))}
            />
            <TextField
              label={t_('Operation name')}
              value={draft.name}
              onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
            />
            <TextField
              label={t_('Description')}
              value={draft.description}
              onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
            />
            <TextField
              select
              required
              label={t_('Technology')}
              value={draft.technologyName}
              onChange={(event) => setDraft((current) => ({ ...current, technologyName: event.target.value }))}
            >
              {technologies.map((option) => (
                <MenuItem key={option.id || option.name} value={option.name}>{option.name}</MenuItem>
              ))}
            </TextField>
            {useIndex
              ? (
                <TextField
                  select
                  label={t_('Index')}
                  value={effectiveIndexValue}
                  onChange={(event) => setDraft((current) => ({ ...current, indexValue: event.target.value }))}
                >
                  {indexOptions.map((option) => (
                    <MenuItem key={option.id || option.name} value={option.name}>{option.name}</MenuItem>
                  ))}
                </TextField>
                )
              : null}
          </Stack>
        }
      />

      <SmartDialog
        setOpen={!!pendingDelete.code}
        title={{ render: <Typography variant='h6'>{t_('Delete operation')}</Typography> }}
        message={
          <Stack spacing={1} data-testid='operation-delete-confirmation'>
            <span>{t_('This will delete the operation and all its versions')}</span>
            {deleteError ? <Alert severity='warning' data-testid='operation-delete-error'>{t_(deleteError)}</Alert> : null}
          </Stack>
        }
        acceptCallback={removeOperation}
        cancelCallback={() => setPendingDelete({})}
        autoClose={false}
      />
    </div>
  )
}

export default OperationLibraryTable
